import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms'
import moment, { Moment } from 'moment'

export function isValidInstitutionskennung(ik: string): boolean {
  const parts = ik.substring(2).split('').map(Number)
  const ikControlNo = parts.pop()

  if (ikControlNo === undefined) {
    return false
  }

  let sum = 0
  parts.forEach((part, position) => {
    const product = part * (position % 2 === 1 ? 1 : 2)
    sum += product > 9 ? product - 9 : product
  })

  return sum % 10 === ikControlNo
}

export function institutionskennungValidator(control: AbstractControl): ValidationErrors | null {
  if (isValidInstitutionskennung(control.value ?? '')) {
    return null
  }
  return { invalidIk: { value: control.value } }
}

/**
 * Gesamtlänge der IBAN je Land. Bewusst nur die Länder, in denen wir Bankverbindungen führen —
 * ein unbekanntes Land wird nur über Struktur und Prüfsumme geprüft, damit eine unvollständige
 * Liste keine echte IBAN ablehnt.
 *
 * Spiegelt `IbanUtil` im Backend (`common/.../util/IbanUtil.java`). Wer hier ein Land ergänzt,
 * ergänzt es dort mit — sonst nimmt das Formular etwas an, das der Server ablehnt.
 */
const IBAN_LENGTH_BY_COUNTRY: Record<string, number> = {
  DE: 22,
  AT: 20,
  CH: 21,
  NL: 18,
  BE: 16,
  LU: 20,
  FR: 27,
  IT: 27,
  PL: 28,
  DK: 18,
}

/**
 * Prüft eine IBAN auf Struktur, Länderlänge und Prüfsumme (ISO 7064 Mod 97-10).
 *
 * Der frühere reine `Validators.pattern` liess `DE676106106626620200` durch — 20 Stellen statt 22.
 * Genau die stand auf TST in den Stammdaten, und die Patientenrechnung kam dann ohne GiroCode
 * heraus, weil der Backend-Service die IBAN zu Recht verwarf (2026-08-05).
 */
export function isValidIban(raw: string): boolean {
  const iban = raw.replace(/\s/g, '').toUpperCase()
  if (!/^[A-Z]{2}\d{2}[0-9A-Z]{11,30}$/.test(iban)) {
    return false
  }
  const expectedLength = IBAN_LENGTH_BY_COUNTRY[iban.substring(0, 2)]
  if (expectedLength !== undefined && iban.length !== expectedLength) {
    return false
  }
  // Ziffernweise Modulo — die Zahl wäre für Number zu groß (bis 34 Zeichen ergeben bis 68 Stellen).
  const rearranged = iban.substring(4) + iban.substring(0, 4)
  let remainder = 0
  for (const char of rearranged) {
    const value = char >= '0' && char <= '9' ? char.charCodeAt(0) - 48 : char.charCodeAt(0) - 55
    remainder = (value > 9 ? remainder * 100 + value : remainder * 10 + value) % 97
  }
  return remainder === 1
}

/** Meldet `{ invalidIban: … }`. Für Felder, die leer bleiben dürfen, in `nurWennGefuellt(...)` wickeln. */
export function ibanValidator(control: AbstractControl): ValidationErrors | null {
  if (isValidIban(control.value ?? '')) {
    return null
  }
  return { invalidIban: { value: control.value } }
}

export function isValidGkvNumber(gkvNumber: string): boolean {
  const CheckEGK = (testling: string): boolean => {
    const m = testling.match(/^([A-Z]{1})([\d]{8})([\d]{1})$/)
    if (m) {
      const letterValue = ('0' + (m[1].charCodeAt(0) - 64)).slice(-2)
      const cardNo = letterValue + m[2]
      let sum = 0
      for (let i = 0; i < 10; i++) {
        let d = parseInt(cardNo[i], 10)
        if (i % 2 === 1) d *= 2
        if (d > 9) d -= 9
        sum += d
      }
      return sum % 10 === parseInt(m[3], 10)
    }
    return false
  }
  return CheckEGK(gkvNumber.toUpperCase())
}

/**
 * Prüft, ob eine GKV-Nummer gültig ist.
 * Struktur: 1 Buchstabe + 8 Ziffern + 1 Prüfziffer
 */
export function gkvValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    /**
     * copied from https://de.wikipedia.org/wiki/Krankenversichertennummer
     * refined with chatty <3
     */
    const CheckEGK = isValidGkvNumber

    const value = control.value?.toUpperCase()
    if (value && !CheckEGK(value)) {
      return { gkvInvalid: true }
    }
    return null
  }
}

export function dateOfBirthFilter(): (d: Moment | null | undefined) => boolean {
  return (d) => {
    if (!d) {
      return false
    }
    // check if d is after '1900-01-01'
    const minDate = moment('1900-01-02')
    const now = moment()
    return d.isBetween(minDate, now, 'day', '[]')
  }
}

export function dateOfBirthValidator(): ValidatorFn {
  const minDate = new Date('1900-01-01')
  const maxDate = new Date()
  return (control: AbstractControl<string | null>): ValidationErrors | null => {
    const value = control.value
    if (!value) {
      return { dateOfBirthInvalid: 'empty' }
    }
    const date = new Date(value)
    if (isNaN(date.getTime())) {
      return { dateOfBirthInvalid: 'invalid' }
    }
    if (date.getTime() < minDate.getTime() || date > maxDate) {
      return { dateOfBirthInvalid: 'outOfRange' }
    }
    return null
  }
}
